import React from 'react';
import { ShieldCheck, Lock, CreditCard } from 'lucide-react';

/**
 * PayPal trust badge component.
 * Variants:
 *   - inline  : single horizontal row of small icons + text (footer, near forms)
 *   - card    : detailed multi-feature card (booking modal, checkout)
 *   - compact : minimal badge (under buttons)
 */

const PayPalIcon = ({ size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 32 32"
    width={size}
    height={size}
    aria-hidden="true"
  >
    <path
      fill="#003087"
      d="M12.5 4h7.7c3.6 0 6.3 1.5 6.3 5.2 0 4.9-3.5 7.7-8.7 7.7H15l-1.1 6.5H9.1L12.5 4z"
    />
    <path
      fill="#0070BA"
      d="M11.2 8h7.7c3.6 0 6.3 1.5 6.3 5.2 0 4.9-3.5 7.7-8.7 7.7h-2.8l-1.1 6.5H7.8L11.2 8z"
    />
  </svg>
);

const VisaIcon = ({ size = 32 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 20" width={size} height={(size * 20) / 32} aria-hidden="true">
    <rect width="32" height="20" rx="3" fill="#1A1F71" />
    <text x="16" y="14" textAnchor="middle" fill="#fff" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="9" fontStyle="italic">VISA</text>
  </svg>
);

const MastercardIcon = ({ size = 32 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 20" width={size} height={(size * 20) / 32} aria-hidden="true">
    <rect width="32" height="20" rx="3" fill="#fff" stroke="#ddd" />
    <circle cx="13" cy="10" r="6" fill="#EB001B" />
    <circle cx="19" cy="10" r="6" fill="#F79E1B" fillOpacity="0.9" />
  </svg>
);

const AmexIcon = ({ size = 32 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 20" width={size} height={(size * 20) / 32} aria-hidden="true">
    <rect width="32" height="20" rx="3" fill="#016FD0" />
    <text x="16" y="14" textAnchor="middle" fill="#fff" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="6.5">AMEX</text>
  </svg>
);

export const PayPalBadge = ({ variant = 'inline', className = '' }) => {
  if (variant === 'card') {
    return (
      <div
        className={`bg-white border border-emerald-100 rounded-xl p-5 shadow-sm ${className}`}
        data-testid="paypal-trust-card"
      >
        <div className="flex items-center space-x-3 mb-4">
          <PayPalIcon size={36} />
          <div>
            <p className="font-heading text-lg text-navy leading-tight">
              Secure Payment with PayPal
            </p>
            <p className="font-body text-xs text-muted-foreground">
              Trusted by 400+ million customers worldwide
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center">
            <ShieldCheck size={20} className="mx-auto text-emerald-700 mb-1" />
            <p className="font-body text-[11px] text-navy leading-tight">
              Buyer<br />Protection
            </p>
          </div>
          <div className="text-center">
            <Lock size={20} className="mx-auto text-emerald-700 mb-1" />
            <p className="font-body text-[11px] text-navy leading-tight">
              256-bit<br />Encrypted
            </p>
          </div>
          <div className="text-center">
            <CreditCard size={20} className="mx-auto text-emerald-700 mb-1" />
            <p className="font-body text-[11px] text-navy leading-tight">
              No Account<br />Needed
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center space-x-2 pt-3 border-t border-emerald-50">
          <span className="font-body text-[10px] uppercase tracking-wider text-muted-foreground">We accept</span>
          <VisaIcon size={28} />
          <MastercardIcon size={28} />
          <AmexIcon size={28} />
          <PayPalIcon size={20} />
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div
        className={`inline-flex items-center space-x-2 ${className}`}
        data-testid="paypal-compact-badge"
      >
        <Lock size={14} className="text-emerald-700" />
        <span className="font-body text-xs text-muted-foreground">
          Secure checkout via
        </span>
        <PayPalIcon size={16} />
        <span className="font-accent text-xs font-bold text-[#003087]">PayPal</span>
      </div>
    );
  }

  // Inline default — for footers, headers, package cards
  return (
    <div
      className={`flex flex-wrap items-center justify-center gap-3 ${className}`}
      data-testid="paypal-inline-badge"
    >
      <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg shadow-sm border border-emerald-100">
        <PayPalIcon size={20} />
        <span className="font-accent text-xs font-bold text-[#003087] uppercase tracking-wider">
          PayPal Verified
        </span>
      </div>
      <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg shadow-sm border border-emerald-100">
        <ShieldCheck size={16} className="text-emerald-700" />
        <span className="font-body text-xs font-semibold text-navy">
          Buyer Protection
        </span>
      </div>
      <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg shadow-sm border border-emerald-100">
        <Lock size={16} className="text-emerald-700" />
        <span className="font-body text-xs font-semibold text-navy">
          SSL Encrypted
        </span>
      </div>
    </div>
  );
};

export default PayPalBadge;
